'use client'

import { useEffect, useRef, useState } from 'react'
import { useMediaQuery } from '@mantine/hooks'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import {
  KTechPioneersImgUrl,
  industryOptions,
  KTechPioneersList,
} from '../_data/ktechpioneers'
import styles from '../ktechpioneers.module.scss'
import CustomHeader from '../_components/PioneersHeader'
import PioneersFooter from '../_components/PioneersFooter'
import {
  IndustryBadges,
  ChallengeCard,
} from '../_components/ChallengeCard'

// ─── Types ──────────────────────────────────────────────────────────────────

type ViewMode = 'grid' | 'list'

// ─── Filter Checkbox (for Industry) ──────────────────────────────────────────

interface FilterCheckboxProps {
  label: string
  checked: boolean
  color: string
  onChange: () => void
}

const FilterCheckbox = ({ label, checked, color, onChange }: FilterCheckboxProps) => (
  <label className="group flex cursor-pointer items-center gap-2 py-1" onClick={onChange}>
    <span
      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border"
      style={{
        borderColor: checked ? color : '#666b74',
        backgroundColor: checked ? color : 'transparent',
      }}>
      {checked && (
        <svg viewBox="0 0 10 8" fill="none" className="h-3 w-3">
          <path
            d="M1 3.5L3.5 6L9 1"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
    <span
      className="text-sm text-white">
      {label}
    </span>
  </label>
)

// ─── Icon Components ──────────────────────────────────────────────────────────

const GridIcon = ({ active }: { active: boolean }) => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 17 17"
    fill="none"
    className={active ? 'text-white' : 'text-[#666b74]'}>
    <rect x="1" y="1" width="6" height="6" rx="1" fill="currentColor" />
    <rect x="10" y="1" width="6" height="6" rx="1" fill="currentColor" />
    <rect x="1" y="10" width="6" height="6" rx="1" fill="currentColor" />
    <rect x="10" y="10" width="6" height="6" rx="1" fill="currentColor" />
  </svg>
)

const ListIcon = ({ active }: { active: boolean }) => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 17 17"
    fill="none"
    className={active ? 'text-white' : 'text-[#666b74]'}>
    <rect x="1" y="2" width="15" height="2.5" rx="1" fill="currentColor" />
    <rect x="1" y="7.5" width="15" height="2.5" rx="1" fill="currentColor" />
    <rect x="1" y="13" width="15" height="2.5" rx="1" fill="currentColor" />
  </svg>
)

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="7" cy="7" r="5" stroke="#666b74" strokeWidth="1.5" />
    <path d="M11 11L14 14" stroke="#666b74" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

// ─── Sidebar Content ──────────────────────────────────────────────────────────

const SidebarContent = ({
  keyword,
  setKeyword,
  selectedIndustry,
  toggleFilter,
  setSelectedIndustry,
}: {
  keyword: string
  setKeyword: (v: string) => void
  selectedIndustry: string[]
  toggleFilter: (value: string, selected: string[], setSelected: (v: string[]) => void) => void
  setSelectedIndustry: (v: string[]) => void
}) => (
  <>
    {/* Search */}
    <div className="relative flex items-center gap-2 rounded-md bg-white/10">
      <input
        type="text"
        placeholder="Enter Keywords"
        value={keyword}
        onChange={e => setKeyword(e.target.value)}
        className="flex-1 bg-transparent pr-8 pl-4 py-2 pc:py-3 text-sm text-[#f0f1f3] placeholder:text-[#666b74] focus:outline-none"
      />
      <div className="absolute right-3 top-1/2 -translate-y-1/2">
        <SearchIcon />
      </div>
    </div>

    {/* INDUSTRY */}
    <div className="mt-4 flex w-full flex-col gap-3 pc:mt-[45px]">
      <div className="flex w-full flex-col gap-1">
        <FilterCheckbox
          label="전체"
          color="#0180FF"
          checked={selectedIndustry.length === 0}
          onChange={() => setSelectedIndustry([])}
        />
        {industryOptions.map(opt => (
          <FilterCheckbox
            key={opt.value}
            label={opt.label}
            color={opt.color}
            checked={selectedIndustry.includes(opt.value)}
            onChange={() => toggleFilter(opt.value, selectedIndustry, setSelectedIndustry)}
          />
        ))}
      </div>
    </div>
  </>
)

// ─── Main Page ────────────────────────────────────────────────────────────────

const PioneersListPage = () => {
  const pathname = usePathname()
  const basePath = pathname.replace(/\/list$/, '') || '/'
  const isMobile = useMediaQuery('(max-width:1279px)')
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [keyword, setKeyword] = useState('')
  const [selectedIndustry, setSelectedIndustry] = useState<string[]>([])
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const sidebarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isMobile || !sidebarOpen) return
    const handleClick = (e: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target as Node)) {
        setSidebarOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [isMobile, sidebarOpen])

  const toggleFilter = (value: string, selected: string[], setSelected: (v: string[]) => void) => {
    setSelected(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value])
  }

  const filteredList = KTechPioneersList.filter(item => {
    const matchesKeyword =
      item.company.toLowerCase().includes(keyword.toLowerCase()) ||
      item.title.toLowerCase().includes(keyword.toLowerCase())
    const matchesIndustry =
      selectedIndustry.length === 0 || selectedIndustry.some(s => item.type.includes(s))
    return matchesKeyword && matchesIndustry
  })

  return (
    <div
      className={`relative flex flex-col min-h-screen w-full min-w-[360px] ${styles.pageRoot}`}
      style={{
        background: 'radial-gradient(ellipse at center, #121535 0%, #0e0e0e 100%)',
      }}>
      <CustomHeader basePath={basePath} />

      <div className="mx-auto flex-1 w-full max-w-7xl px-4 pb-20 pt-[106px] pc:px-6 pc:pb-45 pc:pt-[145px]">
        {/* ── Page Title ─────────────────────────── */}
        <div className="pb-[32px] pc:pb-[46px]">
          <h3 className="mb-3 text-[21px] font-strong text-[#3385FF] pc:text-[28px]">협력 과제</h3>
          <p className="text-xs mb-0.5 pc:text-lg text-white leading-normal">아래의 수요처별 협력 과제를 클릭하여 내용을 확인하신 후, 협력을 희망하는 과제에 지원해 주세요. <br />제시된 협력 과제 외에도 자유 과제 제안·지원이 가능합니다. (지원 횟수 제한 없음)</p>
          <p className="text-xs pc:text-base text-[#969696] leading-normal">* 복수 지원 시, 지원서를 각 기업 페이지에서 개별 제출해 주시기 바랍니다.<br />* 상단의 모집공고문을 다운로드한 후, 지원서 및 필수 제출서류를 작성해 주세요. 작성한 파일은 모두 압축하여 하나의 ZIP 파일로 제출해 주시기 바랍니다.</p>
        </div>

        <div className="relative flex flex-col gap-8 pc:flex-row">
          {/* ══ Mobile Filter Button (sticky) ═════════════════════════════════ */}
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(true)}
              className="fixed bottom-6 right-6 z-50 flex items-center justify-center rounded-full bg-[#0080FF] px-0.5 py-0.5 shadow-lg">
              <p className="px-3 pl-4 text-sm font-semibold text-white">필터</p>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-black">
                <img
                  src={`${KTechPioneersImgUrl}/list_icon_filter.png`}
                  className="h-auto w-4 object-contain"
                  alt="-"
                />
              </div>
            </button>
          )}

          {/* ══ Sidebar ═══════════════════════════════════════════════════════ */}
          {isMobile ? (
            <div
              className={`fixed inset-0 z-50 transition-opacity duration-300 ${sidebarOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
                }`}>
              <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
              <aside
                ref={sidebarRef}
                className={`absolute bottom-0 left-0 top-0 flex w-[280px] flex-col gap-3 overflow-y-auto bg-[#141618] p-5 transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                  }`}>
                <div className="flex w-full flex-row items-center justify-between">
                  <h6 className="text-base font-strong text-[#F0F1F3]">Filter</h6>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="flex h-8 w-8 items-center justify-center text-[#0061F9]">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M1 1l12 12M13 1L1 13"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
                <SidebarContent
                  keyword={keyword}
                  setKeyword={setKeyword}
                  selectedIndustry={selectedIndustry}
                  toggleFilter={toggleFilter}
                  setSelectedIndustry={setSelectedIndustry}
                />
              </aside>
            </div>
          ) : (
            <aside className="flex w-full shrink-0 flex-col gap-3 pc:sticky pc:top-[80px] pc:w-[228px] pc:self-start">
              <SidebarContent
                keyword={keyword}
                setKeyword={setKeyword}
                selectedIndustry={selectedIndustry}
                toggleFilter={toggleFilter}
                setSelectedIndustry={setSelectedIndustry}
              />
            </aside>
          )}

          {/* ══ Main Content ═══════════════════════════════════════════════════ */}
          <div className="min-w-0 flex-1">
            {/* Top bar */}
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-[13px] text-[#9ea3ab]">
                  TOTAL <span className="font-semibold text-[#9ea3ab]">{filteredList.length}</span>
                </span>
                <div className="flex items-center gap-0.5 rounded-md bg-[#1a1c1f] p-0.5">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`rounded p-1.5 transition ${viewMode === 'list' ? 'bg-[#222428]' : ''
                      }`}>
                    <ListIcon active={viewMode === 'list'} />
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`rounded p-1.5 transition ${viewMode === 'grid' ? 'bg-[#222428]' : ''
                      }`}>
                    <GridIcon active={viewMode === 'grid'} />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-[#666b74]">Home</span>
                <img
                  src={`${KTechPioneersImgUrl}/icon_arrow_gray.png`}
                  className="h-auto w-4 object-contain"
                  alt="arrow"
                />
                <span className="text-xs text-[#9ea3ab]">협력 과제</span>
              </div>
            </div>

            {/* Card Grid */}
            {viewMode === 'grid' ? (
              <div className="flex flex-col tb:flex-row tb:flex-wrap gap-4 tb:items-stretch">
                {filteredList.map((item, idx) => (
                  <ChallengeCard key={idx} item={item} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {filteredList.map((item, idx) => (
                  <Link
                    key={idx}
                    href={item.url}
                    className={`${styles.HoverImgScale} flex cursor-pointer flex-row items-center overflow-hidden rounded-lg border border-[#2A2D32] bg-[#141618] transition hover:ring-1 hover:ring-white/50`}>
                    <div className="relative w-[118px] h-[81px] pc:h-[118px] pc:w-[200px] shrink-0 overflow-hidden bg-black">
                      <img
                        src={item.thumbnail}
                        alt={item.company}
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    </div>
                    <div className="flex flex-1 flex-row items-center justify-start gap-3 px-3 py-2 pc:gap-5 pc:px-5 pc:py-3">
                      {!isMobile && (
                        <div
                          className={`${styles.not_grid_list} shrink-0 flex flex-col items-center gap-1`}>
                          <IndustryBadges types={item.type} />
                        </div>
                      )}
                      <div className="flex flex-col gap-1 pc:gap-0 items-start justify-center">
                        <p className="text-sm font-strong text-[#F0F1F3] line-clamp-2 pc:text-base">
                          {item.company}
                        </p>
                        <p className="text-xs text-[#F0F1F3] line-clamp-2 pc:text-sm break-keep">
                          {item.title}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {filteredList.length === 0 && (
              <div className="flex h-64 items-center justify-center text-[15px] text-[#666b74]">
                검색 결과가 없습니다.
              </div>
            )}
          </div>
        </div>
      </div>

      <PioneersFooter />
    </div>
  )
}

export default PioneersListPage
